<template>
  <div class="">
    <div class="flex gap-5 mb-2" :class="{ 'justify-end': tabToRight }">
      <div class="relative">
        <div
          class="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5"
        >
          <button
            v-for="option in options"
            :key="option.value"
            @click="selectPeriod(option)"
            :class="[
              selectedPeriod === option.value
                ? 'shadow-theme-xs text-gray-900 bg-white dark:bg-gray-800'
                : 'text-gray-500 dark:text-gray-400',
              'px-3 py-2 font-medium rounded-md text-theme-sm hover:text-gray-900 hover:shadow-theme-xs  dark:hover:text-white',
            ]"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>

    <div class="max-w-full overflow-auto custom-scrollbar">
      <div :class="{ shimmerElement: loading }">
        <div
          class="-ml-4 pl-2 md:overflow-hidden"
          :class="{
            'min-w-[800px]': selectedPeriod == 'month',
            'min-w-[500px]': selectedPeriod == 'year',
            'min-w-[100%]': selectedPeriod == 'week',
          }"
        >
          <VueApexCharts
            type="bar"
            height="350"
            :options="chartOptions"
            :series="series"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import VueApexCharts from 'vue3-apexcharts'
import { onMounted, ref } from 'vue'

defineProps({ tabToRight: { type: Boolean, default: false } })

// === Options de période ===
const options = [
  {
    value: 'year',
    label: 'Année',
    chartColumntWidth: '70%',
    periodMarks: [
      'Jan',
      'Fév',
      'Mar',
      'Avr',
      'Mai',
      'Jun',
      'Jul',
      'Aoû',
      'Sep',
      'Oct',
      'Nov',
      'Déc',
    ],
  },
  {
    value: 'month',
    label: 'Mois',
    chartColumntWidth: '70%',
    periodMarks: Array.from({ length: 31 }, (_, i) => (i + 1).toString()),
  },
  {
    value: 'week',
    label: 'Semaine',
    chartColumntWidth: '70%',
    periodMarks: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
  },
]

const selectedPeriod = ref('week')
const loading = ref(false)

// === Données simulées pour le rider ===
const sampleStats = {
  week: {
    // total: [30, 28, 32, 25, 40, 38, 20],
    // success: [28, 27, 30, 23, 37, 35, 18],
    // failed: [1, 0, 1, 1, 2, 1, 1],
    // pending: [1, 1, 1, 1, 1, 2, 1],
    total: [0, 0, 0, 0, 0, 0, 0],
    success: [0, 0, 0, 0, 0, 0, 0],
    failed: [0, 0, 0, 0, 0, 0, 0],
    pending: [0, 0, 0, 0, 0, 0, 0],
  },
  month: {
    total: Array.from(
      { length: 31 },
      () => Math.floor(Math.random() * 40) + 20,
    ),
    success: Array.from(
      { length: 31 },
      () => Math.floor(Math.random() * 35) + 15,
    ),
    failed: Array.from({ length: 31 }, () => Math.floor(Math.random() * 5)),
    pending: Array.from({ length: 31 }, () => Math.floor(Math.random() * 3)),
  },
  year: {
    total: Array.from(
      { length: 12 },
      () => Math.floor(Math.random() * 1000) + 500,
    ),
    success: Array.from(
      { length: 12 },
      () => Math.floor(Math.random() * 900) + 400,
    ),
    failed: Array.from({ length: 12 }, () => Math.floor(Math.random() * 100)),
    pending: Array.from({ length: 12 }, () => Math.floor(Math.random() * 50)),
  },
}

// === Données du graphe ===
const series = ref([
  // {
  //   name: 'Total livraisons',
  //   data: [],
  // },
  {
    name: 'Livraisons effectuées',
    data: [],
  },
  {
    name: 'Livraisons non effectuées',
    data: [],
  },
  // {
  //   name: 'En cours',
  //   data: [],
  // },
])

// === Options du graphique ===
const chartOptions = ref({
  legend: {
    show: true,
    position: 'top',
    horizontalAlign: 'left',
    markers: {
      shape: 'circle',
    },
  },
  colors: ['#FF7200',/*  '#FFA200',  */'#FFC260', '#FFD2A0'],
  chart: {
    fontFamily: 'Outfit, sans-serif',
    type: 'bar',
    height: 350,
    stacked: true,
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  plotOptions: {
    bar: {
      columnWidth: '100%',
      horizontal: false,
      borderRadius: 8,
      borderRadiusApplication: 'end',
    },
  },
  dataLabels: {
    enabled: false,
  },
  xaxis: {
    type: 'category',
    categories: [],
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  tooltip: {
    y: {
      title: { formatter: () => '' },
      formatter: val => `${val} livraisons`,
    },
  },
})

// === Fonctions ===
function selectPeriod(option) {
  selectedPeriod.value = option.value
  updateChart(option)
}

function updateChart(option) {
  loading.value = true
  const data = sampleStats[option.value]

  // Simule un temps de chargement
  setTimeout(() => {
    series.value = [
      // { name: 'Total livraisons', data: data.total },
      { name: 'Livraisons effectuées', data: data.success },
      { name: 'Livraisons non effectuées', data: data.failed },
      // { name: 'En cours', data: data.pending },
    ]

    chartOptions.value = {
      ...chartOptions.value,
      xaxis: { ...chartOptions.value.xaxis, categories: option.periodMarks },
      plotOptions: {
        ...chartOptions.value.plotOptions,
        bar: {
          ...chartOptions.value.plotOptions.bar,
          columnWidth: option.chartColumntWidth,
        },
      },
    }

    loading.value = false
  }, 500)
}

onMounted(() => {
  selectPeriod(options[2]) // Par défaut : semaine
})
</script>

<style scoped>
.area-chart {
  width: 100%;
}
</style>
